import { Table, TableBody, TableCell, TableRow } from '../../ui/catalyst/table';
import { Skeleton } from '../../ui/skeleton';
import { TableColumn } from './types';

interface SkeletonTableProps<TData> {
  columns: TableColumn<TData>[];
  rowCount?: number;
  selectionEnabled?: boolean;
  density?: 'normal' | 'compact';
}

/**
 * Skeleton loader for DataTable that matches its exact layout and spacing.
 * Used for optimistic UI updates to prevent layout shift during data fetching.
 */
export const SkeletonTable = <TData,>({
  columns,
  rowCount = 10,
  selectionEnabled = false,
  density = 'normal',
}: SkeletonTableProps<TData>) => {
  return (
    <div
      className="[&_table]:table-fixed [&_table]:w-full overflow-auto flex-1"
      style={{ contain: 'layout' }}
    >
      <Table
        className="[--gutter:--spacing(6)] !overflow-x-visible"
        dense={density === 'compact'}
      >
        <TableBody>
          {Array.from({ length: rowCount }).map((_, rowIndex) => (
            <TableRow
              key={rowIndex}
              className={density === 'compact' ? 'h-6' : 'h-12'}
            >
              {selectionEnabled && (
                <TableCell
                  className="w-12 !pl-4"
                  style={
                    density === 'compact'
                      ? { paddingTop: '4px', paddingBottom: '4px' }
                      : undefined
                  }
                >
                  <Skeleton className="h-4 w-4" />
                </TableCell>
              )}
              {columns.map((column, colIndex) => (
                <TableCell
                  key={colIndex}
                  className={`${
                    density === 'compact'
                      ? 'leading-4 font-medium !py-1'
                      : 'leading-6 font-medium'
                  } ${colIndex === 0 && !selectionEnabled ? '!pl-4' : ''}`}
                  style={{
                    width: column.width || 'auto',
                    ...(density === 'compact'
                      ? { paddingTop: '4px', paddingBottom: '4px' }
                      : undefined),
                  }}
                >
                  <Skeleton className="h-4 w-full max-w-[80%]" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
